import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useGuardedNavigation, useUnsavedGuard } from '../../components/layout/unsavedGuard';
import { useCategories } from '../../hooks/useCategories';
import { supabase } from '../../lib/supabase';
import posthog from '../../lib/posthog';
import {
  Button,
  Spinner,
  SectionDivider,
  gradientFor,
} from '../../components/design-system';
import { PlaceEditorModal } from '../../components/host/PlaceEditorModal';
import { LibraryPickerModal } from '../../components/host/LibraryPickerModal';
import type { Itinerary, HandbookContent, ItineraryDay, ItineraryPlace, EventLibraryItem } from '../../types';
import { getPlacePhotos, photoFields } from '../../lib/placePhotos';

async function notifyTraveler(travelerId: string, tripName: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const url = import.meta.env.VITE_SUPABASE_URL as string;
    await fetch(`${url}/functions/v1/send-push`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({
        user_id: travelerId,
        title: 'Your handbook is ready!',
        body: `Your host just published the handbook for ${tripName}.`,
        data: { type: 'itinerary' },
      }),
    });
  } catch { /* best effort */ }
}

function addDays(date: string, n: number): string {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function newPlace(categorySlug?: string): ItineraryPlace {
  return { id: crypto.randomUUID(), name: '', category: categorySlug ?? 'food', description: '', photos: [], photo_url: '', highlight: false, tip: '', address: '', latitude: null, longitude: null };
}

function isHandbook(c: unknown): c is HandbookContent {
  return !!c && typeof c === 'object' && 'days' in (c as object);
}

function generateDays(start: string, end: string): ItineraryDay[] {
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || s > e) return [];
  const days: ItineraryDay[] = [];
  const cur = new Date(s);
  while (cur <= e) {
    days.push({ date: cur.toISOString().slice(0, 10), places: [] });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function placeFromLibrary(item: EventLibraryItem): ItineraryPlace {
  return {
    id: crypto.randomUUID(),
    name: item.name,
    category: item.category_slug,
    description: item.description,
    ...photoFields(getPlacePhotos(item)),
    highlight: false,
    address: item.address,
    latitude: item.latitude,
    longitude: item.longitude,
    tip: item.tip,
    library_item_id: item.id,
  };
}

interface EditingState {
  dayIdx: number;
  placeIdx: number | null; // null = new event not yet added
  place: ItineraryPlace;
}

interface DestinationLeg {
  city: string;
  start_date?: string | null;
  days: number;
}

interface CityGroup {
  city: string;
  /** Indices into the top-level `days` array belonging to this city. */
  dayIndices: number[];
}

/**
 * Groups the trip's days by which of Alma's destination_days legs each
 * day's date falls in. Returns null whenever the grouping can't be trusted
 * (no legs, or legs without dates to match against) — callers fall back to
 * the plain, unfiltered day-tab row in that case. A day whose date falls
 * outside every leg's range (a gap, or a day added past Alma's plan) is
 * folded into the closest preceding leg so it isn't silently dropped from
 * every tab.
 */
function groupDaysByCity(days: ItineraryDay[], legs: DestinationLeg[] | null): CityGroup[] | null {
  if (!legs || legs.length === 0) return null;
  const ranges = legs
    .filter((l): l is DestinationLeg & { start_date: string } => !!l.start_date)
    .map((l) => ({ city: l.city, start: l.start_date, end: addDays(l.start_date, Math.max(l.days, 1) - 1) }));
  if (ranges.length === 0) return null;

  const groups: CityGroup[] = [];
  const indexOfCity = new Map<string, number>();
  let lastCity = ranges[0].city;
  days.forEach((d, i) => {
    const match = ranges.find((r) => d.date >= r.start && d.date <= r.end);
    const city = match?.city ?? lastCity;
    lastCity = city;
    if (!indexOfCity.has(city)) {
      indexOfCity.set(city, groups.length);
      groups.push({ city, dayIndices: [] });
    }
    groups[indexOfCity.get(city)!].dayIndices.push(i);
  });
  return groups;
}

const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Category slug → thumbnail gradient key. Falls back to 'travel' for unknown slugs. */
function gradientKeyFor(slug: string): 'food' | 'drink' | 'view' | 'nature' | 'landmark' | 'art' | 'shop' | 'travel' {
  const known = ['food', 'drink', 'view', 'nature', 'landmark', 'art', 'shop', 'travel'];
  return (known.includes(slug) ? slug : 'travel') as ReturnType<typeof gradientKeyFor>;
}

// ── Main editor ───────────────────────────────────────────────────────────────

export function ItineraryEditor() {
  const { matchId } = useParams<{ matchId: string }>();
  const { user, loading } = useAuth();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const attempt = useGuardedNavigation();

  const [existingItinerary, setExistingItinerary] = useState<Itinerary | null>(null);
  const [tripName, setTripName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [noteForTraveler, setNoteForTraveler] = useState('');
  const [days, setDays] = useState<ItineraryDay[]>([{ date: new Date().toISOString().slice(0, 10), places: [] }]);
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [travelerName, setTravelerName] = useState<string | null>(null);
  const [tripDays, setTripDays] = useState<number | null>(null);
  const [destinationLegs, setDestinationLegs] = useState<DestinationLeg[] | null>(null);
  // Set once the load finishes, so the first values aren't "unsaved".
  const [loaded, setLoaded] = useState(false);
  // JSON of the content as last loaded/saved; anything different is unsaved.
  const baselineRef = useRef<string | null>(null);
  const noteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [libraryItems, setLibraryItems] = useState<EventLibraryItem[]>([]);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  // Guards the load effect against re-running mid-edit. useAuth's `user`
  // object is a NEW reference on every auth event, including an hourly
  // TOKEN_REFRESHED — this effect used to depend on the whole `user` object,
  // so any background refresh re-fetched the itinerary and overwrote every
  // unsaved local edit (days, places, dates, trip name) with whatever was
  // last saved. That's what was silently discarding a host's work while
  // building a handbook. There's nothing to re-fetch once loaded — the
  // portrait/itinerary/library data doesn't change from outside this editor
  // — so this just needs to run once per matchId.
  const loadedForMatchRef = useRef<string | null>(null);

  const categoryOptions = categories.map((c) => ({ slug: c.slug, name: c.name }));

  useEffect(() => {
    if (!matchId || !user) return;
    if (loadedForMatchRef.current === matchId) return;
    loadedForMatchRef.current = matchId;

    // Sequenced deliberately: the itinerary fetch (the actual saved draft)
    // must resolve and be applied BEFORE the portrait fetch decides whether
    // to prefill generated blank days. These used to be two independent
    // promise chains firing from the same effect run, both gating on the
    // `startDate` component state closed over at the moment the effect
    // started (always ''). Whichever settled last "won" regardless of which
    // one actually had real data — and since the portrait path is two
    // sequential round-trips (matches → taste_portraits) versus one for
    // itineraries, the itinerary usually resolved FIRST in practice, so the
    // portrait callback almost always fired last and stomped the just-loaded
    // draft with freshly-generated empty days. A host would see their saved
    // draft for a moment, then watch it vanish on every page refresh.
    (async () => {
      const { data: itineraryData } = await supabase
        .from('itineraries').select('*').eq('match_id', matchId).eq('host_id', user.id).maybeSingle();
      let hasExistingItinerary = false;
      let existingDestination = '';
      let existingItineraryId: string | null = null;
      let existingContentSnapshot: HandbookContent | null = null;
      if (itineraryData) {
        const it = itineraryData as Itinerary;
        setExistingItinerary(it);
        if (isHandbook(it.content)) {
          hasExistingItinerary = true;
          existingItineraryId = it.id;
          existingContentSnapshot = it.content;
          existingDestination = it.content.destination ?? '';
          setTripName(it.content.trip_name ?? '');
          setDestination(existingDestination);
          setStartDate(it.content.start_date ?? '');
          setEndDate(it.content.end_date ?? '');
          setNoteForTraveler(it.content.note_for_traveler ?? '');
          setDays(it.content.days.length > 0 ? it.content.days : [{ date: new Date().toISOString().slice(0, 10), places: [] }]);
        }
      }

      const { data: matchData } = await supabase
        .from('matches').select('traveler_id, portrait_id, leg:trip_legs(city, start_date, days), profiles!matches_traveler_id_fkey(full_name)')
        .eq('id', matchId).eq('host_id', user.id).single();
      if (matchData) {
        const d = matchData as unknown as {
          traveler_id: string; portrait_id: string | null; profiles: { full_name: string | null } | null;
          leg?: { city: string; start_date: string; days: number } | { city: string; start_date: string; days: number }[] | null;
        };
        setTravelerName(d.profiles?.full_name ?? null);
        // A host of a trip with several cities plans their own city only: its days, not the whole trip's.
        const leg = Array.isArray(d.leg) ? (d.leg[0] ?? null) : (d.leg ?? null);
        if (d.portrait_id) {
          const { data: portrait } = await supabase
            .from('taste_portraits')
            .select('trip_days, start_date, trip_name, portrait_json')
            .eq('id', d.portrait_id).single();
          if (portrait) {
            const p = portrait as {
              trip_days: number | null; start_date: string | null; trip_name: string | null;
              portrait_json: { destinations?: string[]; destination_days?: DestinationLeg[] } | null;
            };
            const numDays = leg ? leg.days : (p.trip_days ?? 3);
            const firstDay = leg ? leg.start_date : p.start_date;
            setTripDays(numDays);
            // City tabs above the day tabs come from Alma's per-city
            // breakdown, not anything the host edits here.
            if (!leg && p.portrait_json?.destination_days?.length) {
              setDestinationLegs(p.portrait_json.destination_days);
            }
            // Backfills destination regardless of hasExistingItinerary —
            // there's no manual "Destination" field anymore (removed with
            // the Trip details block), so any pre-existing draft saved
            // before that removal (or before this backfill existed) with an
            // empty destination would otherwise NEVER get one, permanently
            // losing the handbook's hero illustration since that's keyed
            // off this field.
            if (!existingDestination && (leg || p.portrait_json?.destinations?.length)) {
              const backfilled = leg ? leg.city : (p.portrait_json?.destinations ?? []).join(' & ');
              setDestination(backfilled);
              // Also persist it immediately for an already-saved draft —
              // otherwise the fix only lives in this session's local state,
              // and the traveler-facing handbook (which reads straight from
              // the DB) stays illustration-less until the host happens to
              // hit Save again.
              if (existingItineraryId && existingContentSnapshot) {
                void supabase
                  .from('itineraries')
                  .update({ content: { ...existingContentSnapshot, destination: backfilled }, updated_at: new Date().toISOString() })
                  .eq('id', existingItineraryId);
              }
            }
            // Only prefill trip name/dates/days from the portrait when
            // there's no saved draft to preserve — an existing itinerary's
            // dates/days always win.
            if (!hasExistingItinerary) {
              if (p.trip_name) setTripName(p.trip_name);
              if (firstDay) {
                const end = addDays(firstDay, numDays - 1);
                setStartDate(firstDay);
                setEndDate(end);
                const generated = generateDays(firstDay, end);
                if (generated.length > 0) setDays(generated);
              }
            }
          }
        }
      }
      setLoaded(true);
    })();

    supabase.from('event_library').select('*').eq('host_id', user.id).order('name')
      .then(({ data }) => setLibraryItems((data ?? []) as EventLibraryItem[]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, user?.id]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  function getContent(): HandbookContent {
    return { trip_name: tripName, destination, start_date: startDate || null, end_date: endDate || null, note_for_traveler: noteForTraveler || null, days };
  }

  const serialized = JSON.stringify(getContent());
  // First render after the load finishes becomes the clean baseline.
  useEffect(() => {
    if (loaded && baselineRef.current === null) baselineRef.current = serialized;
  }, [loaded, serialized]);
  const dirty = loaded && baselineRef.current !== null && baselineRef.current !== serialized;

  async function save(status: 'draft' | 'submitted'): Promise<boolean> {
    if (!matchId || !user) return false;
    setError('');
    setSaving(true);
    try {
      const { data: match } = await supabase.from('matches').select('traveler_id').eq('id', matchId).eq('host_id', user.id).single();
      const { error: upsertError, data } = await supabase.from('itineraries').upsert(
        { ...(existingItinerary ? { id: existingItinerary.id } : {}), match_id: matchId, host_id: user.id, traveler_id: match?.traveler_id, content: getContent(), status, updated_at: new Date().toISOString() },
        { onConflict: 'match_id' }
      ).select('*').maybeSingle();
      if (upsertError) throw upsertError;
      if (data) setExistingItinerary(data as Itinerary);
      baselineRef.current = JSON.stringify(getContent());
      if (status === 'submitted' && match?.traveler_id) {
        void notifyTraveler(match.traveler_id, getContent().trip_name || 'your trip');
      }
      // HostMatchLayout's "% built" sidebar card fetches its own copy of the
      // itinerary once on mount and has no other way to learn a save just
      // happened — without this it stays frozen (often at 0%) for the whole
      // session no matter how much gets drafted here.
      window.dispatchEvent(new CustomEvent('sk:itinerary-saved', { detail: { matchId } }));
      const eventProperties = {
        day_count: days.length,
        place_count: days.reduce((count, day) => count + day.places.length, 0),
      };
      if (status === 'draft') {
        posthog.capture('itinerary_draft_saved', eventProperties);
      } else {
        posthog.capture('itinerary_published', eventProperties);
      }
      showToast(status === 'draft' ? 'Draft saved.' : 'Published to traveler!');
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }

  useUnsavedGuard(dirty, () => save('draft'));

  async function saveNote() {
    if (!matchId || !user) return;
    setSavingNote(true);
    if (existingItinerary) {
      // This writes the full content — days/places included, not just the
      // note — and it's debounced on every keystroke, making it the
      // dominant save path in practice. It used to skip the
      // sk:itinerary-saved dispatch entirely (only the explicit `save()`
      // below fired it), so HostMatchLayout's "% built" sidebar card only
      // ever learned about the very first save and stayed stale after
      // every autosave from then on.
      const content: HandbookContent = { ...getContent(), note_for_traveler: noteForTraveler };
      await supabase.from('itineraries').update({ content, updated_at: new Date().toISOString() }).eq('id', existingItinerary.id);
      baselineRef.current = JSON.stringify(content);
      window.dispatchEvent(new CustomEvent('sk:itinerary-saved', { detail: { matchId } }));
      // This is the dominant save path in practice (debounced autosave, not
      // just note edits) — itinerary_draft_saved fired only from the
      // explicit save() button before, so it under-reported how often
      // drafts actually get saved. Never 'published': saveNote never
      // submits, only save() with an explicit status does that.
      posthog.capture('itinerary_draft_saved', {
        day_count: days.length,
        place_count: days.reduce((count, day) => count + day.places.length, 0),
      });
    } else {
      await save('draft');
    }
    setSavingNote(false);
    showToast('Note saved.');
  }

  function updateDay(dayIdx: number, updater: (d: ItineraryDay) => ItineraryDay) {
    setDays((prev) => prev.map((d, i) => i === dayIdx ? updater(d) : d));
  }
  function addDay() { setDays((prev) => [...prev, { date: '', places: [] }]); }
  function removeDay(idx: number) {
    setDays((prev) => prev.filter((_, i) => i !== idx));
    if (activeDayIdx >= days.length - 1) setActiveDayIdx(Math.max(0, days.length - 2));
  }
  function removePlace(dayIdx: number, placeIdx: number) {
    updateDay(dayIdx, (d) => ({ ...d, places: d.places.filter((_, i) => i !== placeIdx) }));
  }
  function movePlace(dayIdx: number, from: number, to: number) {
    updateDay(dayIdx, (d) => {
      if (to < 0 || to >= d.places.length) return d;
      const places = [...d.places];
      const [moved] = places.splice(from, 1);
      places.splice(to, 0, moved);
      return { ...d, places };
    });
  }
  function duplicatePlace(dayIdx: number, placeIdx: number) {
    updateDay(dayIdx, (d) => {
      const source = d.places[placeIdx];
      if (!source) return d;
      const copy = { ...source, id: crypto.randomUUID() };
      const places = [...d.places];
      places.splice(placeIdx + 1, 0, copy);
      return { ...d, places };
    });
  }

  async function saveToLibrary(place: ItineraryPlace) {
    if (!user) return;
    await supabase.from('event_library').insert({
      host_id: user.id,
      name: place.name,
      category_slug: place.category,
      description: place.description,
      address: place.address || null,
      latitude: place.latitude,
      longitude: place.longitude,
      ...photoFields(getPlacePhotos(place)),
      tip: place.tip || null,
    });
    const { data } = await supabase.from('event_library').select('*').eq('host_id', user.id).order('name');
    setLibraryItems((data ?? []) as EventLibraryItem[]);
  }

  // ── Event modal flow ──
  function openNewEvent() {
    setEditing({ dayIdx: activeDayIdx, placeIdx: null, place: newPlace(categoryOptions[0]?.slug) });
  }
  function openEditEvent(placeIdx: number) {
    const place = days[activeDayIdx].places[placeIdx];
    setEditing({ dayIdx: activeDayIdx, placeIdx, place: { ...place } });
  }
  function applyEdit(place: ItineraryPlace) {
    if (!editing) return;
    const { dayIdx, placeIdx } = editing;
    if (placeIdx === null) {
      updateDay(dayIdx, (d) => ({ ...d, places: [...d.places, place] }));
    } else {
      updateDay(dayIdx, (d) => ({ ...d, places: d.places.map((p, i) => i === placeIdx ? place : p) }));
    }
    setEditing(null);
  }
  function deleteEditing() {
    if (!editing || editing.placeIdx === null) return;
    removePlace(editing.dayIdx, editing.placeIdx);
    setEditing(null);
  }
  function pickFromLibrary(item: EventLibraryItem) {
    updateDay(activeDayIdx, (d) => ({ ...d, places: [...d.places, placeFromLibrary(item)] }));
  }

  function formatDayHeading(day: ItineraryDay, i: number) {
    if (day.date) {
      const d = new Date(day.date + 'T12:00:00');
      return `Day ${i + 1} · ${dayLabels[d.getDay()]}, ${d.toLocaleDateString('en', { month: 'long', day: 'numeric' })}`;
    }
    return `Day ${i + 1}`;
  }
  function formatDayShort(day: ItineraryDay, i: number) {
    if (day.date) {
      const d = new Date(day.date + 'T12:00:00');
      return `${dayLabels[d.getDay()]}, ${d.toLocaleDateString('en', { month: 'short', day: 'numeric' })}`;
    }
    return `Day ${i + 1}`;
  }

  const firstName = travelerName?.trim().split(/\s+/)[0] ?? 'Traveler';
  const catName = (slug: string) => categories.find((c) => c.slug === slug)?.name ?? slug;
  const activeDay = days[activeDayIdx];

  // Visual-only progress indicator: fraction of days with at least one place.
  const daysWithPlaces = days.filter((d) => d.places.length >= 1).length;
  const progressPct = Math.min(100, Math.round((daysWithPlaces / Math.max(days.length, 1)) * 100));

  const cityGroups = groupDaysByCity(days, destinationLegs);
  const currentCityIdx = cityGroups ? Math.max(0, cityGroups.findIndex((g) => g.dayIndices.includes(activeDayIdx))) : 0;
  const visibleDayIndices = cityGroups ? cityGroups[currentCityIdx].dayIndices : days.map((_, i) => i);
  if (loading) return <div className="flex-1 bg-app-bg flex items-center justify-center"><Spinner /></div>;

  return (
    <div className="flex-1 bg-app-bg text-app-fg flex flex-col min-h-0">

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-5 md:px-10 py-6 md:py-10 max-w-[760px] mx-auto flex flex-col">

          {/* Back link (desktop) */}
          <button onClick={() => attempt(() => navigate('/host'))}
            className="hidden md:inline-flex items-center gap-1.5 text-app-muted hover:text-app-fg text-xs font-medium transition-colors mb-6 self-start">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to dashboard
          </button>

          {/* Page header */}
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5 mb-8">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-app-muted mb-2">Handbook</p>
              <input
                type="text"
                value={tripName}
                onChange={(e) => setTripName(e.target.value)}
                placeholder="Trip name…"
                className="font-serif text-3xl md:text-[36px] leading-[1.08] tracking-[-0.015em] text-app-fg bg-transparent border-none outline-none w-full placeholder:text-app-muted/60"
              />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5 text-app-muted text-xs">
                <span className="inline-flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                  {travelerName ?? '—'}
                </span>
                {startDate && endDate && (
                  <span className="inline-flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3"><path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zM3.5 8.5v6.75c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25V8.5h-13z" clipRule="evenodd" /></svg>
                    {startDate} → {endDate}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clipRule="evenodd" /></svg>
                  {days.length} day{days.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Action buttons — scroll with the page; full-width pair on phones */}
            <div className="flex flex-wrap items-center gap-2 shrink-0 [&>button]:flex-1 md:[&>button]:flex-none">
              {error && <span className="basis-full md:basis-auto text-xs text-crimson mr-1">{error}</span>}
              <Button variant="secondary" size="sm" onClick={() => void save('draft')} disabled={saving} className="gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                  <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
                  <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
                </svg>
                Save draft
              </Button>
              <Button size="sm" onClick={() => void save('submitted')} disabled={saving || !tripName.trim()} className="gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                  <path d="M3.105 2.289a.75.75 0 00-.826.95l1.903 6.557H13.5a.75.75 0 010 1.5H4.182l-1.903 6.557a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                </svg>
                {saving ? 'Saving…' : existingItinerary?.status === 'submitted' ? 'Send to traveler' : 'Send for review'}
              </Button>
            </div>
          </div>

          {/* Progress bar — always violet, never success/green: this tracks
              how much of the handbook is drafted, not approval status, and
              green is reserved app-wide for actually-approved/accepted
              trips (see matchStatusTone). A fully-built draft going green
              here read as "approved" when it hadn't even been submitted. */}
          <div className="h-[5px] rounded-full bg-app-border overflow-hidden mb-7">
            <div
              className="h-full rounded-full bg-violet transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Host personal note */}
          <div className="rounded-2xl border border-gold/[.18] bg-gold/[.055] px-4 py-4 md:px-[18px] md:py-[15px] mb-8 flex gap-3 items-start">
            <div className="w-[34px] h-[34px] rounded-full shrink-0 border border-app-fg/[.08]"
              style={{ background: 'radial-gradient(circle at 50% 35%, #F08A2E, #A61029 95%)' }} />
            <div className="flex-1 min-w-0">
              <p className="text-app-muted text-[10.5px] mb-1.5">Your personal note to {firstName}</p>
              <textarea
                value={noteForTraveler}
                onChange={(e) => {
                  const v = e.target.value.slice(0, 600);
                  setNoteForTraveler(v);
                  if (noteTimerRef.current) clearTimeout(noteTimerRef.current);
                  noteTimerRef.current = setTimeout(() => void saveNote(), 2000);
                }}
                rows={2}
                placeholder="Write a warm opening note for your traveler — something personal that sets the tone…"
                className="w-full font-serif text-sm leading-[1.6] text-app-fg placeholder:text-app-muted bg-transparent border-none outline-none resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-app-muted">{noteForTraveler.length}/600</span>
                <button onClick={() => void saveNote()} disabled={savingNote}
                  className="text-[11px] font-medium text-app-muted hover:text-app-fg transition-colors disabled:opacity-50">
                  {savingNote ? 'Saving…' : 'Save note'}
                </button>
              </div>
            </div>
          </div>

          {/* City tabs — derived from Alma's destination_days, above the day tabs */}
          {cityGroups && (
            <div className="mb-3">
              {cityGroups.length > 1 ? (
                <div className="flex gap-2 md:flex-wrap overflow-x-auto md:overflow-visible -mx-5 px-5 md:mx-0 md:px-0">
                  {cityGroups.map((g, gi) => (
                    <button key={g.city} onClick={() => setActiveDayIdx(g.dayIndices[0])}
                      className={`shrink-0 whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-medium transition-colors min-h-[36px] ${
                        gi === currentCityIdx ? 'bg-gold/15 text-app-fg border border-gold/40' : 'bg-app-card text-app-fg-2 border border-app-border hover:text-app-fg'
                      }`}>
                      {g.city}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs font-medium text-app-muted">{cityGroups[0].city}</p>
              )}
            </div>
          )}

          {/* Day tabs — filtered to the active city when city tabs are shown */}
          <div className="flex gap-2 md:flex-wrap overflow-x-auto md:overflow-visible -mx-5 px-5 md:mx-0 md:px-0 mb-7">
            {visibleDayIndices.map((i) => (
              <button key={i} onClick={() => setActiveDayIdx(i)}
                className={`shrink-0 whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-medium transition-colors min-h-[44px] md:min-h-[36px] ${
                  activeDayIdx === i ? 'bg-app-fg text-app-bg' : 'bg-app-card text-app-fg-2 hover:text-app-fg'
                }`}>
                Day {i + 1}
              </button>
            ))}
            {!tripDays && !cityGroups && (
              <button onClick={addDay} className="px-3.5 py-2 rounded-full text-xs text-app-muted hover:text-app-fg border border-app-border hover:border-gold/40 transition-colors min-h-[36px]">
                + Day
              </button>
            )}
          </div>

          {/* Active day */}
          {activeDay && (
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-3 mb-4">
                <SectionDivider icon="sun" label={formatDayHeading(activeDay, activeDayIdx)} style={{ flex: 1 }} />
                <span className="text-[11px] text-app-muted shrink-0">
                  {activeDay.places.length} event{activeDay.places.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <input type="date" value={activeDay.date}
                  onChange={(e) => updateDay(activeDayIdx, (d) => ({ ...d, date: e.target.value }))}
                  disabled={!!tripDays}
                  className="text-xs bg-app-card border border-app-border rounded-lg px-2 py-1.5 text-app-fg focus:outline-none disabled:opacity-60" />
                {!tripDays && days.length > 1 && (
                  <button onClick={() => removeDay(activeDayIdx)} className="text-xs text-crimson/70 hover:text-crimson transition-colors">Remove day</button>
                )}
              </div>

              {/* Events list */}
              {activeDay.places.length === 0 ? (
                <p className="text-sm text-app-muted text-center py-8 border border-dashed border-app-border rounded-xl mb-4">
                  No events yet. Add one below.
                </p>
              ) : (
                <div className="flex flex-col gap-2.5 mb-4">
                  {activeDay.places.map((place, placeIdx) => {
                    const g = gradientFor(gradientKeyFor(place.category));
                    return (
                      <div
                        key={place.id}
                        draggable
                        onDragStart={(e) => { setDragIdx(placeIdx); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (dragIdx !== null && dragIdx !== placeIdx) {
                            movePlace(activeDayIdx, dragIdx, placeIdx);
                            setDragIdx(placeIdx);
                          }
                        }}
                        onDragEnd={() => setDragIdx(null)}
                        className={`group flex items-stretch gap-2 border border-app-border rounded-xl p-3.5 hover:border-gold/25 transition-colors ${dragIdx === placeIdx ? 'opacity-60 border-gold/40' : ''}`}
                      >
                        {/* Drag handle — visible on row hover (desktop) */}
                        <div className="hidden md:flex items-center shrink-0 text-app-muted opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                          <i className="ti ti-grip-vertical text-[14px]" />
                        </div>
                        <button onClick={() => openEditEvent(placeIdx)} className="flex items-start gap-3 flex-1 min-w-0 text-left">
                          {place.photo_url ? (
                            <img src={place.photo_url} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0 mt-0.5" loading="lazy"
                              style={place.photo_focal ? { objectPosition: `${place.photo_focal.x}% ${place.photo_focal.y}%` } : undefined} />
                          ) : (
                            <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: g.base }}>
                              <i className={`ti ti-${g.icon} text-cream/60 text-base`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {place.highlight && (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-gold shrink-0">
                                  <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
                                </svg>
                              )}
                              <p className="font-serif text-[14.5px] text-app-fg truncate">{place.name || 'Untitled event'}</p>
                            </div>
                            {place.tip ? (
                              <p className="text-[12.5px] leading-[1.62] text-app-fg-2 mt-1 line-clamp-2">{place.tip}</p>
                            ) : null}
                            <div className="flex items-center gap-3 mt-2 text-app-muted text-[11px]">
                              <span className="inline-flex items-center gap-1 text-[10.5px] font-medium bg-app-card border border-app-border rounded-full px-2 py-0.5 text-app-fg-2">{catName(place.category)}</span>
                            </div>
                          </div>
                        </button>

                        {/* Row actions — duplicate/remove on hover (always visible on touch) */}
                        <div className="flex flex-col justify-center gap-1 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => duplicatePlace(activeDayIdx, placeIdx)}
                            aria-label="Duplicate"
                            title="Duplicate"
                            className="p-1.5 rounded-md text-app-muted hover:text-app-fg transition-colors"
                          >
                            <i className="ti ti-copy text-[14px]" />
                          </button>
                          <button
                            onClick={() => removePlace(activeDayIdx, placeIdx)}
                            aria-label="Remove"
                            title="Remove"
                            className="p-1.5 rounded-md text-app-muted hover:text-crimson transition-colors"
                          >
                            <i className="ti ti-trash text-[14px]" />
                          </button>
                        </div>
                        {/* Mobile reorder fallback (no drag on touch) */}
                        <div className="flex md:hidden flex-col justify-center gap-0.5 shrink-0">
                          <button onClick={() => movePlace(activeDayIdx, placeIdx, placeIdx - 1)} disabled={placeIdx === 0}
                            aria-label="Move up"
                            className="p-1.5 text-app-muted hover:text-app-fg disabled:opacity-25 transition-colors">
                            <i className="ti ti-chevron-up text-[14px]" />
                          </button>
                          <button onClick={() => movePlace(activeDayIdx, placeIdx, placeIdx + 1)} disabled={placeIdx === activeDay.places.length - 1}
                            aria-label="Move down"
                            className="p-1.5 text-app-muted hover:text-app-fg disabled:opacity-25 transition-colors">
                            <i className="ti ti-chevron-down text-[14px]" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add event / from library */}
              <div className="flex flex-wrap gap-2.5 mb-11">
                <button onClick={openNewEvent}
                  className="flex-1 basis-52 flex items-center justify-center gap-2 border border-app-border rounded-full py-2.5 px-3 text-xs font-semibold text-app-fg hover:border-app-fg/40 transition-colors min-h-[44px]">
                  <i className="ti ti-plus text-sm shrink-0" />
                  <span className="truncate">Add event to {formatDayShort(activeDay, activeDayIdx)}</span>
                </button>
                <button onClick={() => setPickerOpen(true)}
                  className="flex items-center justify-center gap-2 border border-app-border rounded-full py-2.5 px-4 text-xs font-semibold text-app-fg hover:border-app-fg/40 transition-colors min-h-[44px]">
                  <i className="ti ti-library text-sm" />
                  From library
                </button>
              </div>
            </div>
          )}

          {/* All-days-at-a-glance summary */}
          {days.length > 1 && (
            <div className="border-t border-app-border pt-6">
              <SectionDivider icon="layout-list" label="All days at a glance" style={{ marginBottom: 16 }} />
              <div className="flex flex-col gap-1.5">
                {days.map((d, i) => {
                  const summary = d.places.length
                    ? d.places.map((p) => p.name || 'Untitled').slice(0, 2).join(', ') + (d.places.length > 2 ? ` +${d.places.length - 2}` : '')
                    : 'No events yet';
                  return (
                    <button key={i} onClick={() => setActiveDayIdx(i)}
                      className="flex items-center gap-3 px-3.5 py-2.5 border border-app-border rounded-xl hover:border-gold/25 hover:bg-app-card transition-colors text-left min-h-[44px]">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: gradientFor(gradientKeyFor(['food', 'drink', 'view', 'nature'][i % 4])).base }}>
                        <span className="font-serif text-[13px] text-cream">{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12.5px] font-medium text-app-fg">{formatDayHeading(d, i)}</p>
                        <p className="text-[11px] text-app-muted mt-0.5 truncate">{summary}</p>
                      </div>
                      <span className={`text-[10.5px] font-medium rounded-full px-2.5 py-0.5 border shrink-0 ${
                        d.places.length >= 1 ? 'bg-success/10 text-success border-success/30' : 'bg-app-card text-app-muted border-app-border'
                      }`}>
                        {d.places.length >= 1 ? 'Ready' : 'Incomplete'}
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5 text-app-muted shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 md:bottom-7 left-1/2 -translate-x-1/2 z-[70] bg-app-fg text-app-bg text-xs font-medium rounded-full px-5 py-2.5 shadow-lg text-center max-w-[calc(100vw-32px)] w-max">
          {toast}
        </div>
      )}

      {/* Event editor modal */}
      {editing && user && (
        <PlaceEditorModal
          place={editing.place}
          isNew={editing.placeIdx === null}
          categoryOptions={categoryOptions}
          hostId={user.id}
          onSave={applyEdit}
          onDelete={deleteEditing}
          onSaveToLibrary={saveToLibrary}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Library picker modal */}
      {pickerOpen && activeDay && (
        <LibraryPickerModal
          items={libraryItems}
          categories={categoryOptions}
          dayLabel={formatDayHeading(activeDay, activeDayIdx)}
          onPick={pickFromLibrary}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default ItineraryEditor;
